import { useCallback, useEffect, useRef } from 'react'
import type { PrimerPastePlan } from '../../../Services/OrderWizard/primerPaste.ts'
import { pasteSummary } from '../../../Services/OrderWizard/analysisSummaries.ts'
import { reportAnalysis, warnOnce } from '../../../Services/OrderWizard/analysisTelemetry.ts'

/* the box takes typing too: a block counts once it has rested this long */
const SETTLE_MS = 1000

/**
 * Reports the paste plan once it settles, or at once when the lab applies or cancels first; the same
 * summary only once. Returns the flush for Apply and Cancel.
 */
export const usePasteTelemetry = (draftId: string, plan: PrimerPastePlan | undefined): (() => void) => {
  const latest = useRef(plan)
  const reported = useRef<string | undefined>(undefined)

  const flush = useCallback(() => {
    const current = latest.current
    if (!current) {
      return
    }
    const summary = pasteSummary(current)
    const signature = JSON.stringify(summary)
    if (signature === reported.current) {
      return
    }
    reported.current = signature
    reportAnalysis(summary)
    if (summary.kind === 'unreadable') {
      warnOnce(draftId, 'paste_unreadable', summary)
    }
  }, [draftId])

  useEffect(() => {
    latest.current = plan
    if (!plan) {
      return
    }
    const timer = setTimeout(flush, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [plan, flush])

  return flush
}
